import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { canonical } from "@/lib/site";

interface ShareBarProps {
  title: string;
  description?: string;
  label?: string;
  /** Absolute URL to share instead of the current page (e.g. a PDF download). */
  url?: string;
}

export default function ShareBar({
  title,
  description,
  label = "Share this article",
  url: urlOverride,
}: ShareBarProps) {
  const location = useLocation();
  const url = urlOverride ?? canonical(location.pathname);
  const text = description ? `${title}, ${description}` : title;
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      setCanNativeShare(true);
    }
    if (typeof navigator !== "undefined") {
      setIsMobile(/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent));
    }
  }, []);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const enc = encodeURIComponent;
  const links = {
    x: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
    reddit: `https://www.reddit.com/submit?url=${enc(url)}&title=${enc(title)}`,
    email: `mailto:?subject=${enc(title)}&body=${enc(`${text}\n\n${url}`)}`,
    sms: `sms:?&body=${enc(`${title} ${url}`)}`,
  };

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
      } catch {
        // ignore
      }
      document.body.removeChild(ta);
    }
  }

  async function handleNativeShare() {
    try {
      await navigator.share({ title, text: description ?? title, url });
    } catch {
      // user dismissed; ignore
    }
  }

  const btnStyle: React.CSSProperties = {
    border: "1px solid hsl(var(--border))",
    borderRadius: "var(--radius)",
    background: "hsl(var(--card))",
    color: "hsl(var(--foreground))",
  };

  return (
    <aside
      aria-label={label}
      className="mt-12 px-5 py-4"
      style={{
        border: "1px solid hsl(var(--border))",
        borderTop: "3px solid hsl(var(--brand))",
        borderRadius: "var(--radius)",
        background: "hsl(var(--secondary) / 0.35)",
      }}
      data-testid="share-bar"
    >
      <div className="flex flex-wrap items-center gap-3">
        <span
          className="text-xs font-semibold uppercase tracking-widest"
          style={{ color: "hsl(var(--brand-text))" }}
        >
          {label}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {canNativeShare && (
            <button
              type="button"
              onClick={handleNativeShare}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm hover:opacity-80 transition-opacity"
              style={btnStyle}
              data-testid="share-native"
              aria-label="Open share menu"
            >
              <ShareIcon /> Share
            </button>
          )}
          <ShareLink href={links.x} label="Share on X" testId="share-x">
            <XIcon />
            <span className="sr-only md:not-sr-only">X</span>
          </ShareLink>
          <ShareLink href={links.facebook} label="Share on Facebook" testId="share-facebook">
            <FacebookIcon />
            <span className="sr-only md:not-sr-only">Facebook</span>
          </ShareLink>
          <ShareLink href={links.linkedin} label="Share on LinkedIn" testId="share-linkedin">
            <LinkedInIcon />
            <span className="sr-only md:not-sr-only">LinkedIn</span>
          </ShareLink>
          <ShareLink href={links.reddit} label="Share on Reddit" testId="share-reddit">
            <RedditIcon />
            <span className="sr-only md:not-sr-only">Reddit</span>
          </ShareLink>
          <a
            href={links.email}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm hover:opacity-80 transition-opacity hover:no-underline"
            style={btnStyle}
            data-testid="share-email"
            aria-label="Share by email"
          >
            <EmailIcon />
            <span className="sr-only md:not-sr-only">Email</span>
          </a>
          {isMobile && (
            <a
              href={links.sms}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm hover:opacity-80 transition-opacity hover:no-underline"
              style={btnStyle}
              data-testid="share-sms"
              aria-label="Share by text message"
            >
              <SmsIcon />
              <span className="sr-only md:not-sr-only">Text</span>
            </a>
          )}
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm hover:opacity-80 transition-opacity"
            style={btnStyle}
            data-testid="share-copy"
            aria-label={copied ? "Link copied" : "Copy link"}
          >
            {copied ? <CheckIcon /> : <LinkIcon />}
            <span>{copied ? "Copied" : "Copy link"}</span>
          </button>
        </div>
      </div>
    </aside>
  );
}

/**
 * Compact icon-only share row for a specific PDF download. Used on tool and
 * download cards where the full ShareBar would be too heavy. Shares the
 * absolute PDF URL, not the page.
 */
export function PdfShareRow({ title, url }: { title: string; url: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const enc = encodeURIComponent;
  const shareTitle = `${title} (free PDF)`;
  const links = {
    x: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(shareTitle)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
    email: `mailto:?subject=${enc(shareTitle)}&body=${enc(`${shareTitle}\n\n${url}`)}`,
  };

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // clipboard unavailable; ignore
    }
  }

  const iconBtn =
    "inline-flex items-center justify-center w-7 h-7 rounded-md border hover:opacity-70 transition-opacity hover:no-underline";
  const iconStyle: React.CSSProperties = {
    borderColor: "hsl(var(--border))",
    background: "hsl(var(--card))",
    color: "hsl(var(--muted-foreground))",
  };

  return (
    <div className="flex items-center gap-1.5" data-testid={`pdf-share-${slugify(title)}`}>
      <span className="text-[11px] uppercase tracking-wider" style={{ color: "hsl(var(--muted-foreground))" }}>
        Share PDF
      </span>
      <a href={links.x} target="_blank" rel="noopener noreferrer" aria-label={`Share ${title} PDF on X`} className={iconBtn} style={iconStyle}>
        <XIcon />
      </a>
      <a href={links.facebook} target="_blank" rel="noopener noreferrer" aria-label={`Share ${title} PDF on Facebook`} className={iconBtn} style={iconStyle}>
        <FacebookIcon />
      </a>
      <a href={links.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`Share ${title} PDF on LinkedIn`} className={iconBtn} style={iconStyle}>
        <LinkedInIcon />
      </a>
      <a href={links.email} aria-label={`Share ${title} PDF by email`} className={iconBtn} style={iconStyle}>
        <EmailIcon />
      </a>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? "PDF link copied" : `Copy link to ${title} PDF`}
        className={iconBtn}
        style={iconStyle}
      >
        {copied ? <CheckIcon /> : <LinkIcon />}
      </button>
    </div>
  );
}

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function ShareLink({
  href,
  label,
  testId,
  children,
}: {
  href: string;
  label: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid={testId}
      className="inline-flex items-center gap-2 px-3 py-2 text-sm hover:opacity-80 transition-opacity hover:no-underline"
      style={{
        border: "1px solid hsl(var(--border))",
        borderRadius: "var(--radius)",
        background: "hsl(var(--card))",
        color: "hsl(var(--foreground))",
      }}
    >
      {children}
    </a>
  );
}

const iconProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "currentColor",
  "aria-hidden": true,
} as const;

function XIcon() {
  return (
    <svg {...iconProps}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg {...iconProps}>
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg {...iconProps}>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.268 2.37 4.268 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function RedditIcon() {
  return (
    <svg {...iconProps}>
      <path d="M12 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 01-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 01.042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 014.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 01.14-.197.35.35 0 01.238-.042l2.906.617a1.214 1.214 0 011.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 00-.231.094.33.33 0 000 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 00.029-.463.33.33 0 00-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 00-.232-.095z" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg {...iconProps}>
      <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
      <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg {...iconProps}>
      <path fillRule="evenodd" clipRule="evenodd" d="M19.902 4.098a3.75 3.75 0 00-5.304 0l-4.5 4.5a3.75 3.75 0 001.035 6.037.75.75 0 01-.646 1.353 5.25 5.25 0 01-1.449-8.45l4.5-4.5a5.25 5.25 0 117.424 7.424l-1.757 1.757a.75.75 0 11-1.06-1.06l1.757-1.757a3.75 3.75 0 000-5.304zm-7.389 4.267a.75.75 0 011-.353 5.25 5.25 0 011.449 8.45l-4.5 4.5a5.25 5.25 0 11-7.424-7.424l1.757-1.757a.75.75 0 111.06 1.06l-1.757 1.757a3.75 3.75 0 105.304 5.304l4.5-4.5a3.75 3.75 0 00-1.035-6.037.75.75 0 01-.354-1z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg {...iconProps}>
      <path fillRule="evenodd" clipRule="evenodd" d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 011.04-.207z" />
    </svg>
  );
}

function SmsIcon() {
  return (
    <svg {...iconProps}>
      <path fillRule="evenodd" clipRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97z" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg {...iconProps}>
      <path d="M13.5 4.5a3 3 0 116 0 3 3 0 01-6 0zm-9 7.5a3 3 0 116 0 3 3 0 01-6 0zm9 7.5a3 3 0 116 0 3 3 0 01-6 0z" />
      <path d="M9.13 10.94l5.74-3.32M9.13 13.06l5.74 3.32" stroke="currentColor" strokeWidth="1.5" fill="none" />
    </svg>
  );
}
